import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { type Locale, format, parseISO } from 'date-fns';
import { fr, enUS, es, it, de } from 'date-fns/locale';
import i18n from '@/i18n';

const DATE_LOCALES: Record<string, Locale> = { fr, en: enUS, es, it, de };

function getDateLocale(): Locale {
  return DATE_LOCALES[i18n.language] || fr;
}

export function getIntlLocale(): string {
  const map: Record<string, string> = { fr: 'fr-FR', en: 'en-GB', es: 'es-ES', it: 'it-IT', de: 'de-DE' };
  return map[i18n.language] || 'fr-FR';
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Montant en euros, dans la locale de l'interface.
 *
 * `digits` : nombre de décimales (2 par défaut, 0 pour un budget annuel, 3
 * pour un coût au kilomètre). C'est le seul endroit où un montant se formate :
 * un gabarit `${x} €` à la main ignore la locale, le séparateur de milliers et
 * l'espace insécable avant le symbole.
 */
export function formatEUR(value: number, options: { digits?: number } = {}): string {
  const digits = options.digits ?? 2;
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/**
 * Pourcentage dans la locale de l'interface. `value` est exprimé en
 * pourcentage (12,5 pour 12,5 %), pas en ratio. `signed` ajoute « + » devant
 * une hausse (le signe moins reste celui de la locale) — pour un écart, pas
 * pour une part.
 */
export function formatPercent(
  value: number,
  options: { digits?: number; signed?: boolean } = {},
): string {
  const digits = options.digits ?? 1;
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
    signDisplay: options.signed ? 'exceptZero' : 'auto',
  }).format(value / 100);
}

/** Nombre localisé, jusqu'à 2 décimales (ex. volumes, comptages). */
/**
 * Minuscules + accents retirés : « Bréger » se trouve en tapant « breger ».
 * Base commune à toutes les recherches et tous les filtres textuels de l'app.
 */
export function normalizeText(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** Montant compact (12 k€, 1,4 M€) : pour une colonne de liste où l'on
 *  compare des ordres de grandeur d'un coup d'œil, pas des centimes. */
export function formatCompactEUR(value: number): string {
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency: 'EUR',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatNumber(n: number, options: { digits?: number } = {}): string {
  return new Intl.NumberFormat(getIntlLocale(), {
    maximumFractionDigits: options.digits ?? 2,
  }).format(n);
}

/** Taille de fichier lisible (B / KB / MB). */
export function formatFileSize(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Postgres renvoie les colonnes `numeric` comme des chaînes à pleine échelle
 * (ex. "1.0000"). On repasse par Number pour retirer les zéros de fin à
 * l'affichage des bornes de tranches.
 */
export function fmtQty(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '+';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n.toString() : String(v);
}

type BandSlot = { minQty: number; maxQty?: number | null; unit: string };

/** Clé stable d'une tranche tarifaire (unité + bornes), pour regroupement. */
export function bandSlotKey(b: BandSlot): string {
  return `${b.unit}|${fmtQty(b.minQty)}|${b.maxQty == null ? '+' : fmtQty(b.maxQty)}`;
}

/** Libellé lisible d'une tranche tarifaire (ex. "0-100 kg"). */
export function bandSlotLabel(b: BandSlot): string {
  return `${fmtQty(b.minQty)}-${b.maxQty == null ? '+' : fmtQty(b.maxQty)} ${b.unit}`;
}

/* Tokens localisés ('P'/'p') et non un pattern littéral : avec 'dd/MM/yyyy',
   la locale date-fns n'avait aucun effet sur l'ordre des composants
   (un utilisateur de-DE voyait 31/07/2026 au lieu de 31.07.2026). */
export function formatDate(dateStr: string): string {
  try {
    const date = parseISO(dateStr);
    return format(date, 'P', { locale: getDateLocale() });
  } catch {
    return dateStr;
  }
}

export function formatDateTime(dateStr: string): string {
  try {
    const date = parseISO(dateStr);
    return format(date, 'P HH:mm', { locale: getDateLocale() });
  } catch {
    return dateStr;
  }
}
